import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { applyContent, cachedContent, setBaseResources } from '@/lib/content-overrides';
import { en, he, NAMESPACES } from '@/locales';

/* ---------------------------------------------------------------------------
   שפת הממשק — עברית (rtl) או אנגלית (ltr).

   המקור: שפת העסק בסשן (tenant.locale). לפני התחברות (כניסה, הרשמה) — מה
   שנבחר בפעם האחרונה בדפדפן הזה, ואחרת עברית. החלפת שפה טוענת את הדף מחדש:
   כך גם טקסטים שמחושבים פעם אחת (מפות תוויות ברמת המודול, Intl) מתעדכנים.
   --------------------------------------------------------------------------- */

export const LOCALES = ['he', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const isLocale = (v: unknown): v is Locale => v === 'he' || v === 'en';

const STORAGE_KEY = 'craftmind.locale';

function stored(): Locale | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
}

export const initialLocale: Locale = stored() ?? 'he';

/**
 * עברית: Intl.PluralRules('he') מחזיר 'two' ל-2, ו-i18next בלי `_two` נופל למפתח
 * הגולמי (לא ל-`_other`) — "נמצאו 2 ספקים" הוצג כ-"import.found". משלימים כאן
 * `_two` (ו-`_many`) מ-`_other`, כך שבקבצים מספיק `_one` / `_other`.
 */
function withHebrewPlurals<T>(tree: T): T {
  if (typeof tree !== 'object' || tree === null) return tree;
  // רשימות (דפים ציבוריים) נשארות רשימות — Object.entries היה הופך אותן לאובייקט.
  if (Array.isArray(tree)) return tree.map((x: unknown) => withHebrewPlurals(x)) as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(tree)) out[k] = withHebrewPlurals(v);
  for (const k of Object.keys(out)) {
    if (!k.endsWith('_other')) continue;
    const base = k.slice(0, -'_other'.length);
    for (const form of ['_two', '_many']) if (!(base + form in out)) out[base + form] = out[k];
  }
  return out as T;
}

const resources = { he: withHebrewPlurals(he), en };
// עותק נקי — המקור שעליו מחילים את הטקסטים שנערכו בקונסולה (content-overrides).
setBaseResources(structuredClone(resources));

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale,
  fallbackLng: 'he',
  ns: NAMESPACES,
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  returnNull: false,
  // המשאבים בתוך ה-bundle — אתחול סינכרוני, לפני הרינדור הראשון.
  initAsync: false,
  // טקסט שנערך בקונסולה נטען אחרי הרינדור הראשון — הרכיבים מתעדכנים בלי רענון.
  react: { bindI18nStore: 'added removed' },
});

// הטקסטים שנערכו, כפי שנשמרו בדפדפן בפעם הקודמת. הגרסה העדכנית נטענת ב-main.tsx.
{
  const cached = cachedContent(initialLocale);
  if (cached) applyContent(initialLocale, cached);
}

applyDocument(initialLocale);

function applyDocument(l: Locale) {
  document.documentElement.lang = l;
  document.documentElement.dir = l === 'he' ? 'rtl' : 'ltr';
}

export const currentLocale = (): Locale => (isLocale(i18n.language) ? i18n.language : 'he');
export const dirOf = (l: Locale = currentLocale()): 'rtl' | 'ltr' => (l === 'he' ? 'rtl' : 'ltr');
/** ל-Intl: תאריכים dd/mm ושקלים גם באנגלית — העסקים בישראל. */
export const intlLocale = (l: Locale = currentLocale()): string => (l === 'he' ? 'he-IL' : 'en-IL');

/**
 * מחליף שפה. `reload` (ברירת מחדל) טוען מחדש כשהשפה באמת השתנתה — ראו למעלה.
 * מחזיר true אם נדרשה טעינה מחדש.
 */
export function setLocale(l: Locale, { reload = true } = {}): boolean {
  // האם הבחירה נשמרה בפועל. אם לא (אחסון חסום, מצב פרטי) — טעינה מחדש לא
  // תעזור: אחרי הטעינה השפה חוזרת לעברית, הסשן אומר אנגלית, וקריאה נוספת
  // ל-setLocale מטעינה שוב — לולאה אינסופית אצל כל עסק אנגלי באחסון חסום.
  let persisted = false;
  try {
    localStorage.setItem(STORAGE_KEY, l);
    persisted = localStorage.getItem(STORAGE_KEY) === l;
  } catch {
    /* פרטי / חסום — מחליפים במקום, בלי טעינה מחדש */
  }
  if (l === currentLocale()) return false;
  if (reload && persisted) {
    window.location.reload();
    return true;
  }
  void i18n.changeLanguage(l);
  applyDocument(l);
  return false;
}

export default i18n;
